import { diffChars, diffLines } from "diff";

/**
 * Character-level ranges for diff highlighting (0-based indices).
 */
export type CharRange = { start: number; end: number };

/**
 * Character-level diff result for a single line pair.
 */
export type CharLevelDiff = {
  removedRanges: CharRange[];
  addedRanges: CharRange[];
};

/**
 * Compute character-level diff between two strings.
 * Returns ranges in the original (removed) and suggested (added) strings.
 */
export function computeCharLevelRanges(
  original: string,
  suggested: string,
): CharLevelDiff {
  const removedRanges: CharRange[] = [];
  const addedRanges: CharRange[] = [];
  let origPos = 0;
  let suggPos = 0;

  const changes = diffChars(original, suggested);
  for (const change of changes) {
    const len = change.value.length;
    if (change.removed) {
      removedRanges.push({ start: origPos, end: origPos + len });
      origPos += len;
    } else if (change.added) {
      addedRanges.push({ start: suggPos, end: suggPos + len });
      suggPos += len;
    } else {
      origPos += len;
      suggPos += len;
    }
  }
  return { removedRanges, addedRanges };
}

/**
 * A single hunk of diff between draft (original) and suggested query.
 * Line numbers are 1-based to match Monaco.
 */
export type DiffHunk = {
  id: number;
  originalStartLine: number;
  originalLines: string[];
  suggestedStartLine: number;
  suggestedLines: string[];
};

function getLinesFromValue(value: string): string[] {
  if (value === "") return [];
  const split = value.split(/\r?\n/);
  if (
    split.length > 0 &&
    split[split.length - 1] === "" &&
    value.endsWith("\n")
  ) {
    split.pop();
  }
  return split;
}

/**
 * Compute line-level diff hunks between draftQuery and suggestedQuery.
 * Returns an array of hunks that can be used for accept/reject per hunk.
 */
export function computeSqlDiff(
  draftQuery: string,
  suggestedQuery: string,
): DiffHunk[] {
  if (!suggestedQuery) return [];

  const changes = diffLines(draftQuery, suggestedQuery);
  const hunks: DiffHunk[] = [];
  let oldLine = 1;
  let newLine = 1;
  let hunkId = 0;
  let pendingRemoved: { startLine: number; lines: string[] } | null = null;

  for (const change of changes) {
    const lines = getLinesFromValue(change.value);
    const lineCount = lines.length;

    if (change.removed) {
      pendingRemoved = { startLine: oldLine, lines };
      oldLine += lineCount;
    } else if (change.added) {
      if (pendingRemoved) {
        hunks.push({
          id: ++hunkId,
          originalStartLine: pendingRemoved.startLine,
          originalLines: pendingRemoved.lines,
          suggestedStartLine: newLine,
          suggestedLines: lines,
        });
        pendingRemoved = null;
      } else {
        hunks.push({
          id: ++hunkId,
          originalStartLine: oldLine,
          originalLines: [],
          suggestedStartLine: newLine,
          suggestedLines: lines,
        });
      }
      newLine += lineCount;
    } else {
      if (pendingRemoved) {
        hunks.push({
          id: ++hunkId,
          originalStartLine: pendingRemoved.startLine,
          originalLines: pendingRemoved.lines,
          suggestedStartLine: newLine,
          suggestedLines: [],
        });
        pendingRemoved = null;
      }
      oldLine += lineCount;
      newLine += lineCount;
    }
  }

  if (pendingRemoved) {
    hunks.push({
      id: ++hunkId,
      originalStartLine: pendingRemoved.startLine,
      originalLines: pendingRemoved.lines,
      suggestedStartLine: newLine,
      suggestedLines: [],
    });
  }

  return hunks;
}
